/**
 * Recent chain selections for the cross-portal chain search (ZC-5733).
 *
 * A small, client-side convenience store: the 5 most recent UNIQUE chains a
 * support user has selected, so they can one-click back into a chain instead of
 * re-typing. Backed by localStorage (same precedent as the SPA TokenStore),
 * keyed per-identity so a shared browser doesn't cross-pollute between staff.
 *
 * DISPLAY-ONLY: selecting a recent still routes through selectCompany, which
 * re-validates (portal, chain) server-side — a stale or forged entry here cannot
 * grant access. Reads parse defensively; corrupt/old/absent data yields [], never
 * an exception (a bad localStorage blob must never break the login SPA).
 */

export interface RecentChain {
  portal_uid: string;
  chain_code: number;
  name: string;
}

const MAX_RECENTS = 5;
const KEY_PREFIX = "zen.auth.recentChains.";

function keyFor(identityId: string): string {
  return `${KEY_PREFIX}${identityId}`;
}

function isRecentChain(value: unknown): value is RecentChain {
  if (typeof value !== "object" || value === null) return false;
  const v = value as Record<string, unknown>;
  return (
    typeof v.portal_uid === "string" &&
    typeof v.chain_code === "number" &&
    typeof v.name === "string"
  );
}

/**
 * The recent chains for an identity, most-recent first, capped at 5. Returns []
 * for any read problem (absent key, corrupt JSON, non-array, malformed entries).
 */
export function getRecents(identityId: string): RecentChain[] {
  let raw: string | null;
  try {
    raw = window.localStorage.getItem(keyFor(identityId));
  } catch {
    // localStorage can throw in private mode / when disabled.
    return [];
  }
  if (raw === null) return [];

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return [];
  }
  if (!Array.isArray(parsed)) return [];

  return parsed.filter(isRecentChain).slice(0, MAX_RECENTS);
}

/**
 * Record a chain selection as the most-recent. Dedupes by portal_uid:chain_code
 * (an existing entry moves to the top, no duplicate) and caps the list at 5.
 * Write errors (quota / private mode) are swallowed — recents are best-effort.
 */
export function addRecent(identityId: string, chain: RecentChain): void {
  const existing = getRecents(identityId);
  const deduped = existing.filter(
    (r) =>
      !(r.portal_uid === chain.portal_uid && r.chain_code === chain.chain_code)
  );
  const next = [
    {
      portal_uid: chain.portal_uid,
      chain_code: chain.chain_code,
      name: chain.name,
    },
    ...deduped,
  ].slice(0, MAX_RECENTS);

  try {
    window.localStorage.setItem(keyFor(identityId), JSON.stringify(next));
  } catch {
    // Best-effort: if we can't persist, recents simply won't update.
  }
}
